import { useCallback, useMemo, useState } from "react";
import { DEFAULT_MESSAGE_TEMPLATES_ROWS_PER_PAGE } from "../constants";
import type { MessageTemplate } from "../types";
import { useMessageTemplateFormFlow } from "./useMessageTemplateFormFlow";
import {
  filterFluxmedDefaultTemplatesByCategory,
  filterMessageTemplatesForDefaultTabView,
  filterMessageTemplatesForOrganizationTabView,
  formatTemplateDisplayName,
  hasTemplateOrganizationPrefix,
  type FluxmedDefaultTemplateCategory,
  type MessageTemplateListScope,
} from "../utils/messageTemplateUiUtils";
import { normalizeTemplateName } from "../validation/messageTemplateValidation";

type ViewMode = "cards" | "table";

interface UseMessageTemplatesSectionStateParams {
  organizationId?: string;
  templateScope?: MessageTemplateListScope;
  hasActiveMetaEndpoint?: boolean;
  templates: MessageTemplate[];
  onCreateTemplate: (params: {
    input: Pick<
      MessageTemplate,
      "name" | "preview" | "header" | "buttonType" | "buttonText" | "buttonValue"
    >;
  }) => Promise<void>;
  onUpdateTemplate: (params: { input: MessageTemplate }) => Promise<void>;
}

export function useMessageTemplatesSectionState({
  organizationId,
  templateScope = "organization",
  hasActiveMetaEndpoint,
  templates,
  onCreateTemplate,
  onUpdateTemplate,
}: UseMessageTemplatesSectionStateParams) {
  const [viewMode, setViewMode] = useState<ViewMode>("cards");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedDefaultCategory, setSelectedDefaultCategory] = useState<
    FluxmedDefaultTemplateCategory | "all"
  >("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(
    DEFAULT_MESSAGE_TEMPLATES_ROWS_PER_PAGE,
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [templateBeingEdited, setTemplateBeingEdited] =
    useState<MessageTemplate | null>(null);
  const [templateIdPendingDelete, setTemplateIdPendingDelete] = useState<
    string | null
  >(null);
  const [templatePendingSend, setTemplatePendingSend] =
    useState<MessageTemplate | null>(null);

  const organizationScopedTemplates = useMemo(() => {
    if (templateScope === "default") {
      return filterMessageTemplatesForDefaultTabView(
        templates,
        organizationId,
        hasActiveMetaEndpoint,
      );
    }

    return filterMessageTemplatesForOrganizationTabView(
      templates,
      organizationId,
      hasActiveMetaEndpoint,
    );
  }, [hasActiveMetaEndpoint, organizationId, templateScope, templates]);

  const availableStatuses = useMemo(
    () =>
      Array.from(
        new Set(
          organizationScopedTemplates
            .map((template) => String(template.status ?? "").trim())
            .filter(Boolean),
        ),
      ),
    [organizationScopedTemplates],
  );

  const filteredTemplates = useMemo(() => {
    const scopedByCategory =
      templateScope === "default"
        ? filterFluxmedDefaultTemplatesByCategory(
            organizationScopedTemplates,
            selectedDefaultCategory,
            organizationId,
            hasActiveMetaEndpoint,
          )
        : organizationScopedTemplates;

    const normalizedQuery = searchQuery.trim().toLowerCase();
    return scopedByCategory.filter((template) => {
      const normalizedStatus = String(template.status ?? "").trim();
      const hasStatusMatch =
        selectedStatuses.length === 0 || selectedStatuses.includes(normalizedStatus);
      if (!hasStatusMatch) return false;

      if (!normalizedQuery) return true;

      const displayName = formatTemplateDisplayName(template.name, {
        organizationPrefix: organizationId,
        hasActiveMetaEndpoint,
      });

      const hasNameMatch = displayName.toLowerCase().includes(normalizedQuery);
      const hasPreviewMatch = template.preview.toLowerCase().includes(normalizedQuery);
      return hasNameMatch || hasPreviewMatch;
    });
  }, [
    hasActiveMetaEndpoint,
    organizationId,
    organizationScopedTemplates,
    searchQuery,
    selectedDefaultCategory,
    selectedStatuses,
    templateScope,
  ]);

  const totalPages = useMemo(() => {
    if (filteredTemplates.length === 0) return 1;
    return Math.ceil(filteredTemplates.length / rowsPerPage);
  }, [filteredTemplates.length, rowsPerPage]);

  const paginatedTemplates = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredTemplates.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, filteredTemplates, rowsPerPage]);

  const createFormFlow = useMessageTemplateFormFlow({
    onSubmitTemplate: async (values) => {
      const normalizedOrganizationId = organizationId?.trim().toLowerCase();
      const normalizedUserName = normalizeTemplateName(values.name);
      const inputName = (() => {
        if (!normalizedOrganizationId) return values.name;
        if (
          hasTemplateOrganizationPrefix({
            templateName: normalizedUserName,
            organizationPrefix: normalizedOrganizationId,
          })
        ) {
          return normalizedUserName;
        }
        return `${normalizedOrganizationId}_${normalizedUserName}`;
      })();

      await onCreateTemplate({
        input: {
          name: inputName,
          header: values.header,
          preview: values.body,
          ...(values.hasButton
            ? {
              buttonType: values.buttonType,
              buttonText: values.buttonText,
              buttonValue: values.buttonValue,
            }
            : {}),
        },
      });
      setIsCreateDialogOpen(false);
    },
  });

  const editFormFlow = useMessageTemplateFormFlow({
    onSubmitTemplate: async (values) => {
      if (!templateBeingEdited) return;
      await onUpdateTemplate({
        input: {
          ...templateBeingEdited,
          name: values.name,
          header: values.header,
          preview: values.body,
          buttonType: values.hasButton ? values.buttonType : undefined,
          buttonText: values.hasButton ? values.buttonText : undefined,
          buttonValue: values.hasButton ? values.buttonValue : undefined,
        },
      });
      setTemplateBeingEdited(null);
    },
  });

  const handleChangeSearchQuery = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  const handleChangeSelectedStatuses = useCallback((statuses: string[]) => {
    setSelectedStatuses(statuses);
    setCurrentPage(1);
  }, []);

  const handleChangeDefaultCategory = useCallback(
    (category: FluxmedDefaultTemplateCategory | "all") => {
      setSelectedDefaultCategory(category);
      setCurrentPage(1);
    },
    [],
  );

  const handleTableRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setCurrentPage(1);
    },
    [],
  );

  const handleEditTemplate = useCallback(
    (template: MessageTemplate) => {
      setTemplateBeingEdited(template);
      editFormFlow.fillFromTemplate(template);
    },
    [editFormFlow],
  );

  return {
    availableStatuses,
    createFormFlow,
    currentPage,
    editFormFlow,
    filteredTemplates,
    handleChangeSearchQuery,
    handleChangeSelectedStatuses,
    handleChangeDefaultCategory,
    handleEditTemplate,
    handleTableRowsPerPageChange,
    selectedDefaultCategory,
    templateScope,
    isCreateDialogOpen,
    paginatedTemplates,
    rowsPerPage,
    searchQuery,
    selectedStatuses,
    setCurrentPage,
    setIsCreateDialogOpen,
    setTemplateBeingEdited,
    setTemplateIdPendingDelete,
    setViewMode,
    templateBeingEdited,
    templateIdPendingDelete,
    templatePendingSend,
    setTemplatePendingSend,
    totalPages,
    viewMode,
  };
}

